/**
 * BYOA — the in-app setup guide.
 *
 * One component, rendered in two places: as a full page on the staff side
 * (ByoaGuideView) and as a collapsible guide inside the customer portal's agent
 * section. It explains, in order, exactly what a person does to connect their
 * own agent — mint a key, point the agent at the endpoint, call the tools — plus
 * the scopes, the security model, and the billing. Content only; no data
 * fetching, so it is safe to drop anywhere.
 */

const CODE_BG = '#0f1430';
const CODE_BORDER = '#2B2BA8';

function Code({ children }: { children: string }) {
  return (
    <pre
      style={{
        background: CODE_BG,
        color: '#e7eaf7',
        border: `1px solid ${CODE_BORDER}`,
        borderRadius: 8,
        padding: '14px 16px',
        margin: '10px 0',
        overflowX: 'auto',
        fontFamily: 'var(--mono, ui-monospace, monospace)',
        fontSize: 12.5,
        lineHeight: 1.6,
        whiteSpace: 'pre',
      }}
    >
      {children}
    </pre>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 14, marginTop: 22 }}>
      <div
        style={{
          flex: 'none',
          width: 30,
          height: 30,
          borderRadius: 999,
          background: 'var(--accent, #2B2BA8)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: 14,
        }}
      >
        {n}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ margin: '2px 0 6px', fontSize: 17 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

// Prose caps at a readable measure; tables and code (their own elements) run the
// full page width, so the guide fills the page without unreadably long lines.
const P: React.CSSProperties = { fontSize: 14, lineHeight: 1.6, color: 'var(--text, inherit)', margin: '6px 0', maxWidth: 900 };
const MUTED: React.CSSProperties = { fontSize: 13, lineHeight: 1.6, opacity: 0.75, margin: '6px 0', maxWidth: 900 };

export function ByoaGuide({ endpoint }: { endpoint?: string }) {
  const ep = endpoint ?? `${typeof window !== 'undefined' ? window.location.origin : 'https://<your-portal>'}/api/portal/mcp`;
  const restBase = ep.replace(/\/mcp$/, '');

  return (
    <div style={{ width: '100%' }}>
      {/* Intro */}
      <p style={{ ...P, maxWidth: 900 }}>
        <strong>BYOA — Bring Your Own Agent</strong> lets your own AI agent drive thevisionqc.
        Point an MCP-capable agent (Claude or your own) at your endpoint, authenticate with a key
        you create here, and it reads your quality and safety data — and, with the right scope, acts
        on your line. Everything the agent does runs under the same tenant isolation and permissions
        a human login has, and every call is metered.
      </p>

      {/* What you need */}
      <div
        style={{
          marginTop: 16,
          padding: '12px 16px',
          borderRadius: 8,
          background: 'var(--panel-2, rgba(127,127,127,0.08))',
          fontSize: 13.5,
          lineHeight: 1.6,
        }}
      >
        <strong>Before you start</strong>
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          <li>You must be the <strong>main contact</strong> for the account — only they can mint or revoke keys.</li>
          <li>An <strong>MCP-capable agent</strong> (e.g. Claude Desktop / an MCP client) — or any HTTP client for the REST route.</li>
          <li>Two minutes. There is nothing to install on our side.</li>
        </ul>
      </div>

      {/* Steps */}
      <Step n={1} title="Create an agent key">
        <p style={P}>
          In the portal, open the <strong>Bring your own agent</strong> section and choose{' '}
          <strong>+ New agent key</strong>. Give it a recognisable name (e.g. “Ops copilot”) and pick an access level:
        </p>
        <ul style={{ ...P, paddingLeft: 18 }}>
          <li><strong>Read only</strong> — the agent can read everything you’re entitled to, but can never act on the line. Start here.</li>
          <li><strong>Read &amp; operate</strong> — additionally lets the agent perform write actions (e.g. acknowledge a safety event). Grant this only to an agent you trust to act.</li>
        </ul>
        <p style={MUTED}>
          The key is shown <strong>once</strong>, at creation. Copy it immediately and store it in your agent’s
          secret store — it cannot be recovered afterwards. Lost it? Revoke it and mint a new one.
        </p>
      </Step>

      <Step n={2} title="Know your endpoint and header">
        <p style={P}>Your MCP endpoint is:</p>
        <Code>{`POST  ${ep}`}</Code>
        <p style={P}>Authenticate every request with the key, as a bearer token or a dedicated header:</p>
        <Code>{`Authorization: Bearer vqc_ag_9f2c…
# or
x-agent-key: vqc_ag_9f2c…`}</Code>
      </Step>

      <Step n={3} title="Connect your agent (MCP)">
        <p style={P}>
          Add thevisionqc as an MCP server in your agent’s configuration. Most MCP clients take an HTTP
          server URL with headers — for example:
        </p>
        <Code>{`{
  "mcpServers": {
    "thevisionqc": {
      "url": "${ep}",
      "headers": { "Authorization": "Bearer vqc_ag_9f2c…" }
    }
  }
}`}</Code>
        <p style={P}>
          The agent then performs the standard handshake — <code>initialize</code>, then{' '}
          <code>tools/list</code> — and the tools appear ready to call. No further setup.
        </p>
      </Step>

      <Step n={4} title="Or call it over plain REST">
        <p style={P}>No MCP client? The same key authenticates the portal’s REST API directly:</p>
        <Code>{`curl -H "Authorization: Bearer vqc_ag_9f2c…" \\
  "${restBase}/quality/summary?window=30d"

# → { "total": 664, "passed": 396, "requiresReview": 73, "manualInspect": 16 }`}</Code>
      </Step>

      <Step n={5} title="What your agent can do">
        <p style={P}>The toolset is curated. Read tools any key may call; the write tool needs operate scope:</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, marginTop: 6 }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={th}>Tool</th>
                <th style={th}>Does</th>
                <th style={th}>Access</th>
              </tr>
            </thead>
            <tbody>
              {TOOLS.map((t) => (
                <tr key={t.name}>
                  <td style={td}><code>{t.name}</code></td>
                  <td style={td}>{t.does}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <span style={{ color: t.operate ? 'var(--warn, #b8860b)' : 'inherit', fontWeight: 600, fontSize: 12 }}>
                      {t.operate ? 'operate' : 'read'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={MUTED}>
          Arguments: <code>window</code> is <code>24h</code>/<code>7d</code>/<code>30d</code>;{' '}
          <code>limit</code> is 1–100; <code>verdict</code> is <code>GOOD</code>/<code>BAD</code>/<code>UNSURE</code>;{' '}
          <code>acknowledge_safety_event</code> needs a required <code>event_id</code>.
        </p>
      </Step>

      <Step n={6} title="Scopes, security and what’s protected">
        <p style={P}>
          A read key has every permission capped at view — it physically cannot operate. If a read key calls a
          write tool, the platform returns a polite refusal (<code>isError: true</code>) and nothing on the line is
          touched. Every query runs under row-level security bound to <strong>your</strong> tenant, so a tool can
          never return, or even name, another customer’s data.
        </p>
        <ul style={{ ...P, paddingLeft: 18 }}>
          <li>Keys are stored only as a SHA-256 hash, shown once, revocable at any time.</li>
          <li>Every call is written to an audit trail.</li>
          <li>Operate is granted deliberately, per key, and only for functions your account is entitled to.</li>
        </ul>
      </Step>

      <Step n={7} title="Watch usage — it’s metered">
        <p style={P}>
          Every agent call is counted. The <strong>Agent activity</strong> table in the portal shows the total and
          a per-key breakdown over the last 30 days. Agent calls are a billable basis, so this is exactly what
          appears on your invoice — no black box.
        </p>
      </Step>

      {/* Troubleshooting */}
      <div style={{ marginTop: 26 }}>
        <h3 style={{ fontSize: 16 }}>Troubleshooting</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, marginTop: 6 }}>
          <tbody>
            <tr><td style={td}><strong>401 Unauthorized</strong></td><td style={td}>The key is missing, mistyped, or sent on the wrong header. Check <code>Authorization: Bearer …</code>.</td></tr>
            <tr><td style={td}><strong>403 / “Agents cannot manage keys”</strong></td><td style={td}>An agent key may read and operate, but never mint, list or revoke keys, or read billing. Do that as the main contact.</td></tr>
            <tr><td style={td}><strong><code>isError: true</code> on a call</strong></td><td style={td}>A read key tried a write tool. Ask the main contact for a read &amp; operate key.</td></tr>
            <tr><td style={td}><strong>Suddenly rejected</strong></td><td style={td}>The key was revoked or expired. Mint a fresh one and update your agent’s config.</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border, rgba(127,127,127,0.3))',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  opacity: 0.7,
};
const td: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border, rgba(127,127,127,0.15))',
  verticalAlign: 'top',
};

const TOOLS: Array<{ name: string; does: string; operate?: boolean }> = [
  { name: 'get_production_overview', does: 'Products with inspection totals and GOOD/BAD/UNSURE counts.' },
  { name: 'get_quality_summary', does: 'Headline counts — total, passed, requires-review, manual-inspect.' },
  { name: 'get_defect_breakdown', does: 'Defect categories with counts over a window.' },
  { name: 'list_recent_inspections', does: 'Most recent inspections with verdict and decision.' },
  { name: 'list_production_batches', does: 'Per-batch records — inspected, OK, NG, start/end.' },
  { name: 'list_safety_cameras', does: 'Your safety cameras and their zone counts.' },
  { name: 'get_safety_events', does: 'Recent safety events with severity and camera.' },
  { name: 'acknowledge_safety_event', does: 'Acknowledge one safety event on the line.', operate: true },
];
